/**
 * Area selection: the desktop editor's beat-range selection (Track.cs `SelectionArea`).
 *
 * A range is two beats, not a list of object indices, so it is resolved to objects only when an
 * operation runs — it cannot drift onto other objects after an edit the way an index selection can
 * (SRVR-0081). Pure functions only; the store holds the state and calls these.
 */
import type { Difficulty, FlashEvent, Note, Obstacle, Slide } from '../types/beatmap';
import { GRID_CENTRE } from '../components/three/placementGeometry';
import { CROUCH_MATCH_MS } from '../components/three/wallGeometry';
import { steppedBeat } from './stepGrid';

/** Track.cs MEASURE_CHECK_TOLERANCE = 1 / (MAX_MEASURE_DIVIDER * 2.9), MAX_MEASURE_DIVIDER = 64. */
export const AREA_BEAT_TOLERANCE = 1 / (64 * 2.9);

export interface AreaRange { startBeat: number; endBeat: number }

/** A held area-selection key. `anchorBeat` is the end that stays put; the other follows the playhead. */
export interface AreaGesture { anchorBeat: number; pressBeat: number; grabbed: boolean }

export const NOTE_FILTER_KINDS = ['right_hand', 'left_hand', 'one_hand_special', 'both_hands_special'] as const;
// In the order the Selection box lists them, which is the mappers' order (SRVR-0318 AC7).
export const WALL_FILTER_KINDS = [
  'wall_left', 'wall_right', 'angle_left', 'angle_right', 'center', 'crouch', 'square', 'triangle',
] as const;
// Unity's event toggles are Lights and Effects: Lights are the web's shared `flashEvents`, Effects the
// active difficulty's flash-effect beats (SRVR-0318 AC8).
export const EVENT_FILTER_KINDS = ['lights', 'effects'] as const;
export type NoteFilterKind = typeof NOTE_FILTER_KINDS[number];
export type WallFilterKind = typeof WALL_FILTER_KINDS[number];
export type EventFilterKind = typeof EVENT_FILTER_KINDS[number];
export type AreaFilterKind = NoteFilterKind | WallFilterKind | EventFilterKind;
export type AreaFilters = Record<AreaFilterKind, boolean>;

/** SelectionOptions.cs: every toggle starts on, and none is persisted. */
export const DEFAULT_AREA_FILTERS: AreaFilters = Object.fromEntries(
  [...NOTE_FILTER_KINDS, ...WALL_FILTER_KINDS, ...EVENT_FILTER_KINDS].map(k => [k, true]),
) as AreaFilters;

export function inRange(beat: number, range: AreaRange): boolean {
  return beat >= range.startBeat - AREA_BEAT_TOLERANCE && beat <= range.endBeat + AREA_BEAT_TOLERANCE;
}

export function nearBeat(a: number, b: number): boolean {
  return Math.abs(a - b) <= AREA_BEAT_TOLERANCE;
}

/** The range in force: a held key's live range (press beat to playhead), else the committed one. */
export function effectiveAreaRange(
  range: AreaRange | null, gesture: AreaGesture | null, playheadBeat: number,
): AreaRange | null {
  if (!gesture) return range;
  return {
    startBeat: Math.min(gesture.anchorBeat, playheadBeat),
    endBeat: Math.max(gesture.anchorBeat, playheadBeat),
  };
}

/**
 * A beat as the desktop editor writes it in its selection banner: the whole beat, then the fraction
 * in its simplest form, nothing when the beat is whole — `2`, `2 1/4`, `3 2/3`. Track.cs
 * `MeasureIndicatorStringCreator` with `simplify` set: the fraction is counted in 64ths, or in 48ths
 * when it is not a whole number of 64ths (a triplet), then halved while both parts are even and the
 * denominator is above 2. One BPM zone, so the zone beat is the chart beat.
 */
export function formatBeat(beat: number): string {
  let whole = Math.floor(beat + 1e-6);
  const in64 = (beat - whole) * 64;
  let num: number;
  let den: number;
  if (Math.abs(in64 - Math.round(in64)) < 1e-3) { num = Math.round(in64); den = 64; }
  else { num = Math.round((in64 / 64) * 48); den = 48; }
  if (num >= den) { whole += 1; num = 0; }
  while (den > 2 && num % 2 === 0 && den % 2 === 0 && num > 0) { num /= 2; den /= 2; }
  return num === 0 ? String(whole) : `${whole} ${num}/${den}`;
}

export function noteFilterKind(n: Note): NoteFilterKind {
  const t = n.note_type as NoteFilterKind;
  if ((NOTE_FILTER_KINDS as readonly string[]).includes(t)) return t;
  return n.hand === 'left' ? 'left_hand' : 'right_hand';
}

/** A crouch list entry is a time in ms; its beat is in the same chart frame as `time_beats`. */
export function msToBeat(ms: number, bpm: number): number {
  return (ms / 1000) * (bpm / 60);
}

/** Indices, per collection, of the objects a range holds. `crouches` index `diff.crouches`. */
export interface RangeContents {
  notes: number[]; slides: number[]; crouches: number[]; squares: number[]; triangles: number[];
}

export function isEmptyContents(c: RangeContents): boolean {
  return !c.notes.length && !c.slides.length && !c.crouches.length && !c.squares.length && !c.triangles.length;
}

/**
 * SRVR-0317: an object is in the range by its head beat, ends included within the tolerance; a rail
 * goes whole with its head. `filters` null means every kind (a paste's clearing ignores the toggles).
 * Bookmarks, jumps and decor groups are never part of a range. Lights are not in a difficulty, so they
 * are resolved separately (`lightsInRange`).
 */
export function objectsInRange(
  diff: Difficulty, range: AreaRange, bpm: number, filters: AreaFilters | null,
): RangeContents {
  const on = (k: AreaFilterKind) => !filters || filters[k];
  const out: RangeContents = { notes: [], slides: [], crouches: [], squares: [], triangles: [] };
  diff.notes.forEach((n, i) => { if (on(noteFilterKind(n)) && inRange(n.time_beats, range)) out.notes.push(i); });
  (diff.slides ?? []).forEach((s, i) => {
    const kind = (WALL_FILTER_KINDS as readonly string[]).includes(s.wall_type) ? s.wall_type as WallFilterKind : null;
    if (kind && on(kind) && inRange(s.time_beats, range)) out.slides.push(i);
  });
  if (on('crouch')) (diff.crouches ?? []).forEach((t, i) => { if (inRange(msToBeat(t, bpm), range)) out.crouches.push(i); });
  if (on('square')) (diff.squares ?? []).forEach((o, i) => { if (inRange(o.time_beats, range)) out.squares.push(i); });
  if (on('triangle')) (diff.triangles ?? []).forEach((o, i) => { if (inRange(o.time_beats, range)) out.triangles.push(i); });
  return out;
}

// ── Copy, remove, paste ────────────────────────────────────────────────────

/** A crouch from the crouch list together with its `crouch_obstacles` position entry, if it has one. */
export interface ClipCrouch { time_ms: number; obstacle: Obstacle | null }

export interface AreaObjects {
  notes: Note[]; slides: Slide[]; crouches: ClipCrouch[]; squares: Obstacle[]; triangles: Obstacle[];
}

/** What a range copy stores (SRVR-0319 AC5/AC6): the objects, the lights (SRVR-0319 AC12), the range
 *  start as anchor, its length. */
export interface AreaClipboard extends AreaObjects { lights: FlashEvent[]; effects?: number[]; anchorBeat: number; lengthBeats: number }

function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) as T; }

function crouchObstacleIndex(obstacles: Obstacle[] | undefined, timeMs: number): number {
  return obstacles ? obstacles.findIndex(o => Math.abs(o.time_ms - timeMs) <= CROUCH_MATCH_MS) : -1;
}

export function copyContents(diff: Difficulty, c: RangeContents): AreaObjects {
  return clone({
    notes: c.notes.map(i => diff.notes[i]),
    slides: c.slides.map(i => diff.slides[i]),
    crouches: c.crouches.map(i => {
      const t = diff.crouches[i];
      const oi = crouchObstacleIndex(diff.crouch_obstacles, t);
      return { time_ms: t, obstacle: oi >= 0 ? diff.crouch_obstacles![oi] : null };
    }),
    squares: c.squares.map(i => diff.squares![i]),
    triangles: c.triangles.map(i => diff.triangles![i]),
  });
}

/** The difficulty without the listed objects; a removed crouch takes its position entry with it. */
export function removeContents(diff: Difficulty, c: RangeContents): Difficulty {
  const keep = (idxs: number[]) => { const s = new Set(idxs); return (_: unknown, i: number) => !s.has(i); };
  const removedCrouches = c.crouches.map(i => diff.crouches[i]);
  const next: Difficulty = {
    ...diff,
    notes: diff.notes.filter(keep(c.notes)),
    slides: (diff.slides ?? []).filter(keep(c.slides)),
    crouches: (diff.crouches ?? []).filter(keep(c.crouches)),
  };
  if (diff.squares) next.squares = diff.squares.filter(keep(c.squares));
  if (diff.triangles) next.triangles = diff.triangles.filter(keep(c.triangles));
  if (diff.crouch_obstacles) {
    next.crouch_obstacles = diff.crouch_obstacles.filter(o => !removedCrouches.some(t => Math.abs(o.time_ms - t) <= CROUCH_MATCH_MS));
  }
  return withCounts(next);
}

type Timed = { time_ms: number; time_seconds: number; time_beats: number; position?: number[] | null };

/** Every object moved so the clipboard's anchor lands on `targetBeat`, its times re-derived at `bpm`
 *  (SRVR-0320 AC1, AC3). Rail nodes and wall positions carry encoded time (ms × 0.02) and move with it. */
export function retimeObjects(objs: AreaObjects, anchorBeat: number, targetBeat: number, bpm: number): AreaObjects {
  const dBeat = targetBeat - anchorBeat;
  const msOf = (b: number) => (b / bpm) * 60000;
  function retime<T extends Timed>(o: T): T {
    const beat = o.time_beats + dBeat;
    const ms = msOf(beat);
    const out: T = { ...o, time_beats: beat, time_ms: ms, time_seconds: ms / 1000 };
    if (o.position && o.position.length >= 3) out.position = [o.position[0], o.position[1], o.position[2] + (ms - o.time_ms) * 0.02, ...o.position.slice(3)];
    return out;
  }
  const c = clone(objs);
  return {
    notes: c.notes.map(n => {
      const r = retime(n);
      const dEnc = (r.time_ms - n.time_ms) * 0.02;
      r.position = [n.position[0], n.position[1], r.time_ms * 0.02];
      if (n.rail_nodes) r.rail_nodes = n.rail_nodes.map(([x, y, z]) => [x, y, z + dEnc] as [number, number, number]);
      return r;
    }),
    slides: c.slides.map(retime),
    crouches: c.crouches.map(cr => {
      const ms = msOf(msToBeat(cr.time_ms, bpm) + dBeat);
      return { time_ms: ms, obstacle: cr.obstacle ? retime({ ...cr.obstacle, time_beats: msToBeat(cr.obstacle.time_ms, bpm) }) : null };
    }),
    squares: c.squares.map(retime),
    triangles: c.triangles.map(retime),
  };
}

/** Only the objects whose head beat is not after `lastBeat` (SRVR-0320 AC4). */
export function objectsUpTo(objs: AreaObjects, lastBeat: number, bpm: number): AreaObjects {
  const ok = (b: number) => b <= lastBeat + AREA_BEAT_TOLERANCE;
  return {
    notes: objs.notes.filter(n => ok(n.time_beats)),
    slides: objs.slides.filter(s => ok(s.time_beats)),
    crouches: objs.crouches.filter(c => ok(msToBeat(c.time_ms, bpm))),
    squares: objs.squares.filter(o => ok(o.time_beats)),
    triangles: objs.triangles.filter(o => ok(o.time_beats)),
  };
}

export function countObjects(objs: AreaObjects): number {
  return objs.notes.length + objs.slides.length + objs.crouches.length + objs.squares.length + objs.triangles.length;
}

/** The difficulty with `objs` added, every collection back in time order. */
export function insertObjects(diff: Difficulty, objs: AreaObjects): Difficulty {
  const byTime = (a: { time_ms: number }, b: { time_ms: number }) => a.time_ms - b.time_ms;
  const next: Difficulty = {
    ...diff,
    notes: [...diff.notes, ...objs.notes].sort(byTime),
    slides: [...(diff.slides ?? []), ...objs.slides].sort(byTime),
    crouches: [...(diff.crouches ?? []), ...objs.crouches.map(c => c.time_ms)].sort((a, b) => a - b),
  };
  if (diff.squares || objs.squares.length) next.squares = [...(diff.squares ?? []), ...objs.squares].sort(byTime);
  if (diff.triangles || objs.triangles.length) next.triangles = [...(diff.triangles ?? []), ...objs.triangles].sort(byTime);
  const crouchObs = objs.crouches.map(c => c.obstacle).filter((o): o is Obstacle => !!o);
  if (diff.crouch_obstacles || crouchObs.length) next.crouch_obstacles = [...(diff.crouch_obstacles ?? []), ...crouchObs].sort(byTime);
  return withCounts(next);
}

/** The difficulty's counts and note-derived durations, recomputed as `setDiffNotes` does. */
export function withCounts(d: Difficulty): Difficulty {
  return {
    ...d,
    note_count: d.notes.length,
    rail_count: d.notes.filter(n => n.has_rail).length,
    slide_count: d.slides.length,
    crouch_count: d.crouches.length,
    duration_beats: d.notes.length ? Math.max(...d.notes.map(n => n.time_beats)) : 0,
    duration_seconds: d.notes.length ? Math.max(...d.notes.map(n => n.time_seconds)) : 0,
  };
}

// ── Flips (Track.FlipSelected, FlipWallsInList; DuckFuncs.GetMirrorXPos/YPos; IWall.CalcNewWallPos) ──

export type FlipAxis = 'x' | 'y';

/** GetMirrorXPos / GetMirrorYPos: mirror about the format's true centre, not about 0. */
export const mirrorX = (x: number) => 2 * GRID_CENTRE[0] - x;
export const mirrorY = (y: number) => 2 * GRID_CENTRE[1] - y;

/** IWall.GetFlippedWallTag: only the side-locked types have an opposite. */
export const FLIPPED_WALL_TYPE: Record<string, string> = {
  wall_left: 'wall_right', wall_right: 'wall_left', angle_left: 'angle_right', angle_right: 'angle_left',
};
/** The file's slideType for each slide wall type (backend synth_parser.WALL_TYPES). */
export const SLIDE_FILE_TYPE: Record<string, number> = {
  wall_right: 0, wall_left: 1, angle_right: 2, center: 3, angle_left: 4,
};
/** IWall.CalcNewWallPos: where a Y-flipped wall's pivot moves, by its type before the flip. */
export const Y_FLIP_OFFSET: Record<string, [number, number]> = {
  wall_left: [-1.094, -0.195713],
  wall_right: [1.094, -0.195713],
  angle_left: [-(0.64551 + 0.031681), -0.28],
  angle_right: [0.64551 + 0.031681, -0.28],
  center: [0, -0.301435],
  square: [0, 0.5101562],
  triangle: [0, 1.2746417 + 0.0114189],
  crouch: [0, -2.059621],
};

function mod360(z: number): number { const r = ((z % 360) + 360) % 360; return r === 0 ? 0 : r; }

/** Track.FlipSelected for one note. Positions are not clamped, as in the desktop editor. */
export function flipNote(n: Note, axis: FlipAxis): Note {
  const out = clone(n);
  if (axis === 'x') {
    out.position = [mirrorX(n.position[0]), n.position[1], n.position[2]];
    const kind = noteFilterKind(n);
    if (kind === 'right_hand') { out.note_type = 'left_hand'; out.hand = 'left'; }
    else if (kind === 'left_hand') { out.note_type = 'right_hand'; out.hand = 'right'; }
    if (n.rail_nodes) out.rail_nodes = n.rail_nodes.map(([x, y, z]) => [mirrorX(x), y, z] as [number, number, number]);
  } else {
    out.position = [n.position[0], mirrorY(n.position[1]), n.position[2]];
    if (n.rail_nodes) out.rail_nodes = n.rail_nodes.map(([x, y, z]) => [x, mirrorY(y), z] as [number, number, number]);
  }
  return out;
}

/** FlipWallsInList for one wall. `type` is its kind (a slide's `wall_type`, or crouch/square/triangle);
 *  `timeMs` gives a wall with no position its encoded z. */
export function flipWall(
  type: string, position: number[] | null | undefined, zRotation: number | undefined, timeMs: number, axis: FlipAxis,
): { type: string; position: number[] | null; z_rotation: number } {
  const newType = FLIPPED_WALL_TYPE[type] ?? type;
  const z = zRotation ?? 0;
  if (axis === 'x') {
    // The centre mirrors onto itself, so a wall drawn at the centre for want of a position keeps none.
    const pos = position && position.length >= 2 ? [mirrorX(position[0]), ...position.slice(1)] : null;
    return { type: newType, position: pos, z_rotation: mod360(-z) };
  }
  const base = position && position.length >= 2 ? position : [GRID_CENTRE[0], GRID_CENTRE[1]];
  const [ox, oy] = Y_FLIP_OFFSET[type] ?? [0, 0];
  const rest = base.length >= 3 ? base.slice(2) : [timeMs * 0.02];
  return { type: newType, position: [base[0] + ox, mirrorY(base[1]) + oy, ...rest], z_rotation: mod360(-(z + 180)) };
}

/** Flip every object (SRVR-0321). The caller has already applied the Selection Options. */
export function flipObjects(objs: AreaObjects, axis: FlipAxis, bpm: number): AreaObjects {
  const c = clone(objs);
  const ob = (o: Obstacle, kind: string): Obstacle => {
    const f = flipWall(kind, o.position, o.z_rotation, o.time_ms, axis);
    return { ...o, position: f.position, z_rotation: f.z_rotation };
  };
  return {
    notes: c.notes.map(n => flipNote(n, axis)),
    slides: c.slides.map(s => {
      const f = flipWall(s.wall_type, s.position, s.z_rotation, s.time_ms, axis);
      const out: Slide = { ...s, wall_type: f.type, z_rotation: f.z_rotation };
      if (f.position) out.position = f.position;
      if (f.type in SLIDE_FILE_TYPE) out.slide_type = SLIDE_FILE_TYPE[f.type];
      return out;
    }),
    crouches: c.crouches.map(cr => {
      if (cr.obstacle) return { ...cr, obstacle: ob(cr.obstacle, 'crouch') };
      if (axis === 'x') return cr;   // drawn at the centre, which mirrors onto itself
      const f = flipWall('crouch', null, 0, cr.time_ms, 'y');
      return { ...cr, obstacle: {
        time_ms: cr.time_ms, time_seconds: cr.time_ms / 1000, time_beats: msToBeat(cr.time_ms, bpm),
        position: f.position, z_rotation: f.z_rotation, initialized: true,
      } };
    }),
    squares: c.squares.map(o => ob(o, 'square')),
    triangles: c.triangles.map(o => ob(o, 'triangle')),
  };
}

// ── Paste behaviour (SelectionOptions.cs: Delete Existing / Prioritize Existing / Prioritize Pasted) ──

/** The desktop editor's three paste behaviours. `deleteExisting` is its default. */
export type PasteMode = 'deleteExisting' | 'prioritizeExisting' | 'prioritizePasted';
export const PASTE_MODES: readonly PasteMode[] = ['deleteExisting', 'prioritizeExisting', 'prioritizePasted'];

/** Track.cs MIN_OVERLAP_DISTANCE: two notes closer than this (straight-line, in note units) overlap. */
export const MIN_OVERLAP_DISTANCE = 0.15;

const isSpecial = (n: Note) => { const k = noteFilterKind(n); return k === 'one_hand_special' || k === 'both_hands_special'; };
const overlaps = (a: Note, b: Note) =>
  Math.hypot(a.position[0] - b.position[0], a.position[1] - b.position[1], a.position[2] - b.position[2]) < MIN_OVERLAP_DISTANCE;

/**
 * Track.ResolveConflictNotesForPaste: the notes a beat holds after a paste lands on it, from the
 * notes being pasted there and the notes already there. A port, branch for branch.
 */
export function resolveNoteConflicts(pasted: Note[], existing: Note[], mode: PasteMode): Note[] {
  if (mode === 'deleteExisting') return [...pasted];
  if (pasted.length === 0) return [...existing];
  const prioritizePasted = mode === 'prioritizePasted';
  if (pasted.length === 1) {
    const p = pasted[0];
    if (prioritizePasted) {
      // The pasted note wins; an existing note survives only if it is the other hand, not a special,
      // and not overlapping it. A pasted special clears the beat.
      if (isSpecial(p)) return [p];
      return [p, ...existing.filter(e => noteFilterKind(e) !== noteFilterKind(p) && !isSpecial(e) && !overlaps(p, e))];
    }
    // The existing notes win; the pasted note is added only if it is a hand note, nothing existing is
    // a special or the same hand, and nothing existing overlaps it.
    if (isSpecial(p)) return [...existing];
    for (const e of existing) {
      if (isSpecial(e) || noteFilterKind(e) === noteFilterKind(p)) return [...existing];
    }
    return existing.some(e => overlaps(p, e)) ? [...existing] : [...existing, p];
  }
  // Several notes pasted on one beat.
  if (prioritizePasted) return [...pasted];
  if (existing.length === 0) return [...pasted];
  if (existing.length === 1) {
    const e = existing[0];
    const out = [e];
    for (const p of pasted) {
      if (isSpecial(p) || noteFilterKind(p) === noteFilterKind(e) || isSpecial(e) || overlaps(p, e)) continue;
      out.push(p);
    }
    return out;
  }
  // Two or more existing notes: they stand.
  return [...existing];
}

/**
 * Land `objs` (already re-timed to their target beats) on `diff` under a paste behaviour (SRVR-0323).
 * `span` is the clipboard's target span, which Delete Existing clears of every kind first. The two
 * Prioritize behaviours touch only the beats something is pasted onto: notes resolve beat by beat
 * through `resolveNoteConflicts`, and a pasted wall replaces (Prioritize Pasted) or yields to
 * (Prioritize Existing) whatever wall is already on its beat.
 */
export function pasteObjects(diff: Difficulty, objs: AreaObjects, span: AreaRange, mode: PasteMode, bpm: number): Difficulty {
  if (mode === 'deleteExisting') {
    return insertObjects(removeContents(diff, objectsInRange(diff, span, bpm, null)), objs);
  }
  const keep: AreaObjects = { notes: [], slides: [], crouches: [], squares: [], triangles: [] };
  const drop: RangeContents = { notes: [], slides: [], crouches: [], squares: [], triangles: [] };

  // Notes, one landing beat at a time.
  const beats: number[] = [];
  for (const n of objs.notes) if (!beats.some(b => nearBeat(b, n.time_beats))) beats.push(n.time_beats);
  for (const b of beats) {
    const at = objectsInRange(diff, { startBeat: b, endBeat: b }, bpm, null).notes;
    const resolved = resolveNoteConflicts(objs.notes.filter(n => nearBeat(n.time_beats, b)), at.map(i => diff.notes[i]), mode);
    // What survives of the existing notes stays where it is; only what the paste brings in is added.
    const kept = new Set(resolved);
    for (const i of at) if (!kept.has(diff.notes[i])) drop.notes.push(i);
    keep.notes.push(...resolved.filter(n => !at.some(i => diff.notes[i] === n)));
  }

  // Walls: the desktop editor holds one wall per beat, of any kind.
  const wallBeats = [
    ...objs.slides.map(o => o.time_beats), ...objs.squares.map(o => o.time_beats),
    ...objs.triangles.map(o => o.time_beats), ...objs.crouches.map(c => msToBeat(c.time_ms, bpm)),
  ];
  const occupied = (b: number) => {
    const c = objectsInRange(diff, { startBeat: b, endBeat: b }, bpm, null);
    return { ...c, notes: [] as number[] };
  };
  const wallFree = (b: number) => { const c = occupied(b); return !c.slides.length && !c.crouches.length && !c.squares.length && !c.triangles.length; };
  const take = (b: number) => wallFree(b) || mode === 'prioritizePasted';
  if (mode === 'prioritizePasted') {
    for (const b of wallBeats) {
      const c = occupied(b);
      drop.slides.push(...c.slides); drop.crouches.push(...c.crouches); drop.squares.push(...c.squares); drop.triangles.push(...c.triangles);
    }
  }
  keep.slides = objs.slides.filter(o => take(o.time_beats));
  keep.squares = objs.squares.filter(o => take(o.time_beats));
  keep.triangles = objs.triangles.filter(o => take(o.time_beats));
  keep.crouches = objs.crouches.filter(c => take(msToBeat(c.time_ms, bpm)));

  const uniq = (a: number[]) => Array.from(new Set(a));
  const cleared = removeContents(diff, {
    notes: uniq(drop.notes), slides: uniq(drop.slides), crouches: uniq(drop.crouches),
    squares: uniq(drop.squares), triangles: uniq(drop.triangles),
  });
  return insertObjects(cleared, keep);
}

// ── Lights (Unity's `lights` list; the web's shared `flashEvents`) ─────────

/** Two lights are on the same beat when this close — the window `addFlashEvent` already uses. Unity
 *  compares beats exactly; the web stores them as floating beats. */
export const LIGHT_BEAT_MATCH = 0.01;

/** The lights a range operation takes: those in the range, and only while Lights is on. Unity gates
 *  copy and delete on `lightsEventsSelected` alike, and its paste-clear too (`Track.cs` 14854 checks
 *  the toggle even with `ignoreSelectionOptions`), so there is no "every kind" form here. */
export function lightsInRange(events: readonly FlashEvent[], range: AreaRange, filters: AreaFilters): FlashEvent[] {
  if (!filters.lights) return [];
  return events.filter(e => inRange(e.time_beats, range));
}

/** `events` without the ones in `taken` (by identity). */
export function removeLights(events: readonly FlashEvent[], taken: readonly FlashEvent[]): FlashEvent[] {
  const drop = new Set(taken);
  return events.filter(e => !drop.has(e));
}

/**
 * Land copied lights (`Track.cs` PasteAction): each moved by the paste's offset, in every paste mode
 * alike (Unity's per-mode conflict code for lights is commented out), skipping one whose beat already
 * has a light and any past `lastBeat`. Sorted by beat.
 */
export function pasteLights(
  events: readonly FlashEvent[], clip: readonly FlashEvent[], fromBeat: number, toBeat: number, lastBeat = Infinity,
): FlashEvent[] {
  const out = [...events];
  for (const l of clip) {
    const beat = l.time_beats - fromBeat + toBeat;
    if (beat > lastBeat + AREA_BEAT_TOLERANCE) continue;
    if (out.some(e => Math.abs(e.time_beats - beat) < LIGHT_BEAT_MATCH)) continue;
    out.push({ ...l, time_beats: beat });
  }
  return out.sort((a, b) => a.time_beats - b.time_beats);
}

// ── Effects (the active difficulty's flash-effect beats) ────────────────────

/** The effects a range operation takes: those in the range, and only while Effects is on. As with
 *  lights, a paste's clearing honours the toggle too, so there is no "every kind" form. */
export function effectsInRange(beats: readonly number[], range: AreaRange, filters: AreaFilters): number[] {
  if (!filters.effects) return [];
  return beats.filter(b => inRange(b, range));
}

/** `beats` without the ones in `taken`. */
export function removeEffects(beats: readonly number[], taken: readonly number[]): number[] {
  const drop = new Set(taken);
  return beats.filter(b => !drop.has(b));
}

/** Land copied effects, each moved by the paste's offset: skipping one whose beat already has an
 *  effect and any past `lastBeat`, as `pasteLights` does. Sorted. */
export function pasteEffects(
  beats: readonly number[], clip: readonly number[], fromBeat: number, toBeat: number, lastBeat = Infinity,
): number[] {
  const out = [...beats];
  for (const c of clip) {
    const beat = c - fromBeat + toBeat;
    if (beat > lastBeat + AREA_BEAT_TOLERANCE) continue;
    if (out.some(b => Math.abs(b - beat) < LIGHT_BEAT_MATCH)) continue;
    out.push(beat);
  }
  return out.sort((a, b) => a - b);
}

// ── Note Fill (Track.cs `FillSelectionWithNote`) ─────────────────────────────

/** Track.cs MIN_OVERLAP_DISTANCE: a note of another type within this of the new note's position is replaced. */
export const FILL_OVERLAP_DISTANCE = 0.15;

const isSpecialKind = (k: NoteFilterKind) => k === 'one_hand_special' || k === 'both_hands_special';

/**
 * The notes already on a beat that a fill of `kind` removes before it places its note at the centre
 * (`GetNotesAtBeatThatConflictWithTypeAndPos`, position 0, 0). `existing` must already be the notes
 * the Selection Options let the fill see: a type that is switched off there is never touched.
 *   - a Special fill removes everything on the beat;
 *   - otherwise a note of the same type goes whatever its position, a note of the other hand goes only
 *     if it sits within FILL_OVERLAP_DISTANCE of the centre, and a Special note always goes.
 */
export function fillConflicts(existing: readonly Note[], kind: NoteFilterKind): Note[] {
  if (isSpecialKind(kind)) return [...existing];
  return existing.filter(n => {
    const k = noteFilterKind(n);
    return k === kind || isSpecialKind(k) || Math.hypot(n.position[0], n.position[1]) < FILL_OVERLAP_DISTANCE;
  });
}

/**
 * The beats a fill places a note on: the range's start, then one Step at a time while the beat is not
 * past the range's end. Steps follow the Step grid, which is what the desktop's "step, but never past
 * the next whole beat" comes to for every step that divides a beat.
 */
export function fillBeats(range: AreaRange, step: number): number[] {
  const out: number[] = [];
  let beat = range.startBeat;
  while (beat <= range.endBeat + 1e-6) {
    out.push(beat);
    const next = steppedBeat(beat, step, 1);
    if (!(next > beat)) break;
    beat = next;
  }
  return out;
}
